import React, { useState } from 'react';
import ConfirmNewAddress from '@/components/Modals/ConfirmAddress/ConfirmNewAddress';
import CepTextField from '@/components/NumberFormat/cepTextField';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { KeyboardBackspace } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  IconButton,
  LinearProgress,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { IAddressData } from '.';

interface INewAddressProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  addressData: IAddressData;
  updateAddressData: (company: IAddressData) => void;
  onSuccess: () => void;
}

interface INewDataAddress {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
}

export default function NewAddess({
  isOpen,
  setIsOpen,
  onSuccess,
  addressData,
  updateAddressData,
}: INewAddressProps) {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const { setIsLoading } = useLoading();
  const [cep, setCep] = useState('');
  const progress = 40;
  const [openModalConfirmNewAddress, setOpenModalConfirmNewAddress] =
    useState(false);
  const [cepError, setCepError] = useState(false);

  const handleValidateCep = () => {
    if (cep !== '') {
      setIsLoading(true);
      api
        .get<INewDataAddress | { erro: boolean }>(
          `https://viacep.com.br/ws/${cep}/json/`
        )
        .then(response => {
          if ('erro' in response.data)
            return Toast('error', 'Que pena! CEP não encontrado!');

          const newDataAddress = response.data;
          updateAddressData(
            Object.assign(addressData, {
              city: newDataAddress.localidade,
              neighbourhood: newDataAddress.bairro,
              street: newDataAddress.logradouro,
              zipcode: newDataAddress.cep,
              state: newDataAddress.uf,
              complement: '',
              number: '',
            })
          );

          setOpenModalConfirmNewAddress(true);
        })
        .catch(() => {
          Toast(
            'error',
            'Não foi possível encontrar o cep, tente novamente mais tarde!'
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setCepError(true);
    }
  };

  return (
    <>
      <Modal
        aria-labelledby="modal-my-account"
        aria-describedby="modal-my-account-description"
        open={isOpen}
      >
        <>
          <Box
            sx={{
              backgroundColor: '#00A8F3',
              height: '100vh',
              pt: mdUp ? 0 : 4,
            }}
          >
            <Box
              sx={{
                height: '100%',
                backgroundColor: '#FFFFFF',
                borderRadius: mdUp ? 0 : '12px 12px 0 0',
              }}
            >
              <Box
                sx={{
                  maxWidth: theme.breakpoints.values.md,
                  mx: 'auto',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <IconButton onClick={() => setIsOpen(false)} sx={{ zIndex: 2 }}>
                  <KeyboardBackspace sx={{ color: '#000000' }} />
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                >
                  Novo endereço
                </Typography>
              </Box>
              {mdUp && <Divider />}
              <Box
                sx={{
                  margin: '0 auto',
                  padding: mdUp ? '16px 28px' : '0 28px',
                  maxWidth: theme.breakpoints.values.md,
                }}
              >
                <LinearProgress variant="determinate" value={progress} />
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateRows: `1fr ${mdUp ? '1fr' : 'auto'}`,
                  gap: mdUp ? '50px' : '0px',
                  height: 'calc(100% - 40px - 32px - 6px)',
                  maxWidth: '420px',
                  margin: '0 auto',
                  px: 2,
                }}
              >
                <Box sx={{ alignSelf: mdUp ? 'end' : 'center' }}>
                  <Typography
                    sx={{ mb: 2, fontWeight: '700' }}
                    color="#808080"
                    variant="h5"
                  >
                    Informe o CEP da empresa
                  </Typography>
                  <CepTextField
                    label="CEP"
                    variant="standard"
                    value={cep}
                    error={cepError}
                    helperText={cepError && 'O campo não pode ser vazio!'}
                    onValueChange={value => {
                      setCepError(false);
                      setCep(value);
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '420px',
                    gridRowStart: 2,
                    gridRowEnd: 3,
                    mx: 'auto',
                    mb: '38px',
                  }}
                >
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => handleValidateCep()}
                  >
                    próximo
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      </Modal>
      {addressData && (
        <ConfirmNewAddress
          isOpen={openModalConfirmNewAddress}
          setIsOpen={() => setOpenModalConfirmNewAddress(false)}
          onSuccess={() => {
            onSuccess();
            setIsOpen(false);
          }}
          newDataAddress={addressData}
        />
      )}
    </>
  );
}

export type { INewDataAddress, INewAddressProps };
